import animate from '@play-co/timestep-core/lib/animate';
import { playExtraMovesOfferAnimation } from '../movesPinch/extraMovesOffer.animation';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import { animDuration, delay, withLoading } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import Match2AdContinuePanel from './Match2AdContinuePanel';
import Match2SocialContinueBuyPanel from './Match2SocialContinueBuyPanel';
import { getMatch2ContinueConfig } from 'src/replicant/match2/match2.getters';
import { user } from 'src/StateObserver';
import { showRewardedAd } from 'src/game/ads/ads.sequence';
import { getMaximumAdContinueMoves } from 'src/game/ads/ads.util';
import { GemsWallet } from 'src/game/components/GemsWallet';
import { movesPinchCheck } from 'src/lib/pinch';

import { AssetGroup } from '@play-co/timestep-core';

type CloseHandler = (result: {
  watchedAd?: boolean;
  purchase?: boolean;
}) => void;

const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2AdContinue extends PopupBasic {
  private contentWrapper: View;
  /** Gem offer panel; only its move quote varies for eligible retries. */
  private buyPanel: Match2SocialContinueBuyPanel;
  private gemsWallet: GemsWallet;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;
  /** Regular amount displayed by the buy panel before the bonus reveal. */
  private readonly regularMoves: number;
  /** Bonus passed to init, revealed after the popup opens. */
  private extraMoves = 0;

  constructor(opts: { superview: View; close: CloseHandler }) {
    const offset = 150;
    super({
      superview: opts.superview,
      close: () => opts.close({ purchase: false }),
      ...ui.popup,
      width: 630,
      height: ui.popup.height - offset + 20,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.AD_CONTINUE.POPUP,
      },
    });

    this.message.removeFromSuperview();
    this.title.updateOpts({ size: 30 });
    this.bg.onClick = () => this.toggleShowPuzzle();

    this.gemsWallet = new GemsWallet({
      superview: this.root,
      tracking: { feature: 'header', $subFeature: 'buy-gems' },
    });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    const state = user();
    const { moves, price } = getMatch2ContinueConfig(state);
    this.regularMoves = moves;
    this.buyPanel = new Match2SocialContinueBuyPanel({
      superview: this.contentWrapper,
      amount: moves,
      continues: state.match2.continues,
      x: 165,
      y: 205,
    });

    // Buy button
    new ButtonScaleViewWithText({
      ...ui.okButton,
      x: 40,
      y: ui.okButton.y - offset,
      width: 265,
      superview: this.contentWrapper,
      labelOffsetX: -30,
      icon: {
        image: 'assets/ui/hud/icons/hud_gem.png',
        width: 47,
        height: 40,
        offset: {
          x: 90,
          y: 0,
        },
      },
      localeText: () => `${i18n('basic.buy')} ${price}`,
      onClick: async () => {
        sounds.sfx.play('ok');
        const success = await movesPinchCheck(price, 'match2');
        if (success) opts.close({ purchase: true });
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.AD_CONTINUE.CLOSE,
      },
    });

    // Ad button
    new ButtonScaleViewWithText({
      ...uiConfig.buttons.blue,
      x: 326,
      y: ui.okButton.y - offset,
      width: 265,
      height: 90,
      fontSize: 40,
      labelOffsetY: -2,
      labelOffsetX: 60,
      labelWidth: 200,
      font: 'Body',
      icon: {
        image: 'assets/ui/shared/icons/icon_ad.png',
        width: 60,
        height: 60,
        offset: {
          x: -90,
          y: -2,
        },
      },
      superview: this.contentWrapper,
      localeText: () => i18n('match2.adContinue.adCTA'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.AD_CONTINUE.WATCH,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        const watchedAd = await withLoading(() => showRewardedAd(), {
          feature: Feature.MATCH2._,
          subFeature: Feature.MATCH2.WACTH_REWARDED_AD,
        });
        if (watchedAd) opts.close({ watchedAd });
      },
    });

    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 100 + this.box.style.y + this.box.style.height * 0.5 + 100,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      // Preload the supplied sparkle before the offer animation starts.
      'assets/ui/match2/movepinch/sparkle.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/shared/icons/icon_ad.png',
      'assets/ui/match2/common/panel_pinch.png',
      'assets/ui/shared/icons/arrow_right.png',
    ]);
  }

  public init({ extraMoves = 0 }: { extraMoves?: number }) {
    super.init({ title: i18n('match2.adContinue.title') });

    // Retain the bonus for the opening animation.
    this.extraMoves = extraMoves;

    new Match2AdContinuePanel({
      superview: this.contentWrapper,
      x: 465,
      y: 205,
      // The total that can be earned from watching ads
      amount: getMaximumAdContinueMoves(),
    });
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;
    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    // Wait for the opening tween before taking over its input blocker.
    await super.fadeIn();
    await animate(this.box).toPromise();
    this.overlay.show();
    await this.tweenToggleMessageScaleY(1);

    await playExtraMovesOfferAnimation({
      badge: this.buyPanel.movesBadge,
      label: this.buyPanel.movesLabel,
      regularMoves: this.regularMoves,
      setNumMoves: (moves) => this.buyPanel.setNumMoves(moves),
      bonusMoves: this.extraMoves,
    });
    this.overlay.hide();
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
    await delay(animDuration * 2);
  }
}
